import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Container, IconButton, InputAdornment,
  Paper, TextField, Typography, alpha
} from '@mui/material';
import { ContentCopy, Lock, Mail, Security, Visibility, VisibilityOff } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import createAppTheme from '../theme';
import { pendingQuickExpensePath } from '../services/nativeExpenseService';

const PRIMARY = '#0052CC';
const SECONDARY = '#1A73E8';
const LOGIN_THEME = createAppTheme('light', PRIMARY);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState('credentials');
  const [mfaToken, setMfaToken] = useState('');
  const [setup, setSetup] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { completeAuthentication, login, startMfaSetup, user, verifyMfaLogin, verifyMfaSetup } = useAuth();
  const navigate = useNavigate();
  const afterLogin = pendingQuickExpensePath();

  React.useEffect(() => {
    if (user) navigate(afterLogin, { replace: true });
  }, [user, navigate, afterLogin]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      if (mode === 'credentials') {
        const result = await login(email, password);
        if (!result.success) throw new Error(result.message || 'Credenciales inválidas.');
        if (result.mfa_required) {
          setMfaToken(result.mfa_token);
          setMode('challenge');
        } else if (result.mfa_enrollment_required) {
          setMfaToken(result.mfa_token);
          const enrollment = await startMfaSetup(result.mfa_token);
          if (!enrollment.success) throw new Error(enrollment.message);
          setSetup(enrollment);
          setMode('enrollment');
        }
      } else if (mode === 'challenge') {
        const result = await verifyMfaLogin(mfaToken, code);
        if (!result.success) throw new Error(result.message);
      } else if (mode === 'enrollment') {
        const result = await verifyMfaSetup(mfaToken, code);
        if (!result.success) throw new Error(result.message);
        setPendingSession(result);
        setRecoveryCodes(result.recovery_codes || []);
        setMode('recovery');
      }
    } catch (requestError) {
      setError(requestError.message || 'Error al conectar con el servidor.');
    } finally {
      setIsLoading(false);
    }
  };

  const restart = () => {
    setMode('credentials');
    setMfaToken('');
    setSetup(null);
    setCode('');
    setError('');
  };

  if (user) return null;

  const title = mode === 'credentials' ? 'Bienvenido'
    : mode === 'challenge' ? 'Verificación en dos pasos'
      : mode === 'enrollment' ? 'Protege tu cuenta' : 'Guarda tus códigos';
  const subtitle = mode === 'credentials' ? 'Ingresa tus credenciales para continuar'
    : mode === 'challenge' ? 'Usa Authenticator o un código de recuperación'
      : mode === 'enrollment' ? 'Escanea el QR y confirma el código de 6 dígitos'
        : 'Cada código funciona una sola vez. Guárdalos fuera de este dispositivo.';

  return (
    <Box sx={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center',
      pt: 'max(32px, env(safe-area-inset-top, 0px))',
      pb: 'max(32px, env(safe-area-inset-bottom, 0px))',
      pl: 'env(safe-area-inset-left, 0px)',
      pr: 'env(safe-area-inset-right, 0px)',
      overflowY: 'auto',
      background: 'linear-gradient(145deg, #003A99 0%, #0052CC 50%, #1A73E8 100%)'
    }}>
      <ThemeProvider theme={LOGIN_THEME}>
        <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper elevation={0} sx={{
          p: { xs: 2.5, sm: 5 }, borderRadius: 4, background: 'rgba(255,255,255,.97)',
          color: 'text.primary',
          boxShadow: '0 24px 70px rgba(0,24,72,.35)',
          '& .MuiInputBase-input': {
            color: '#0f172a',
            WebkitTextFillColor: '#0f172a'
          },
          '& .MuiInputBase-input.Mui-disabled': {
            opacity: 1,
            WebkitTextFillColor: 'rgba(15,23,42,.68)'
          }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box sx={{
              width: 58, height: 58, borderRadius: 2, display: 'grid', placeItems: 'center', color: 'white',
              background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`,
              boxShadow: `0 10px 28px ${alpha(PRIMARY, .35)}`
            }}>
              {mode === 'credentials' ? <Lock fontSize="large" /> : <Security fontSize="large" />}
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={850} color={PRIMARY}>{title}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>{subtitle}</Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            {mode === 'credentials' && <>
              <TextField
                fullWidth required autoFocus type="email" label="Correo electrónico" autoComplete="email"
                value={email} onChange={(event) => setEmail(event.target.value)} disabled={isLoading}
                InputProps={{ startAdornment: <InputAdornment position="start"><Mail color="primary" /></InputAdornment> }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth required label="Contraseña" autoComplete="current-password"
                type={showPassword ? 'text' : 'password'} value={password}
                onChange={(event) => setPassword(event.target.value)} disabled={isLoading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Lock color="primary" /></InputAdornment>,
                  endAdornment: <InputAdornment position="end"><IconButton
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton></InputAdornment>
                }}
              />
            </>}

            {mode === 'enrollment' && setup && <Box sx={{ textAlign: 'center' }}>
              <Box component="img" src={setup.qr_data_url} alt="Código QR para Authenticator"
                sx={{ width: 230, maxWidth: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>Clave manual</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all', mb: 2 }}>
                {setup.manual_key}
              </Typography>
            </Box>}

            {['challenge', 'enrollment'].includes(mode) && <TextField
              fullWidth required autoFocus label="Código de verificación" value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              autoComplete="one-time-code" inputMode="numeric" disabled={isLoading}
              helperText={mode === 'challenge' ? 'Acepta 6 dígitos o un código de recuperación.' : ''}
            />}

            {mode === 'recovery' && <>
              <Alert severity="warning" sx={{ mb: 2 }}>No volveremos a mostrar estos códigos.</Alert>
              <Box sx={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, p: 2,
                bgcolor: 'grey.100', borderRadius: 2, fontFamily: 'monospace'
              }}>
                {recoveryCodes.map((recoveryCode) => <Typography key={recoveryCode} fontFamily="inherit">
                  {recoveryCode}
                </Typography>)}
              </Box>
              <Button startIcon={<ContentCopy />} sx={{ mt: 1 }}
                onClick={() => navigator.clipboard?.writeText(recoveryCodes.join('\n'))}>
                Copiar códigos
              </Button>
            </>}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {mode !== 'recovery' ? <Button
              type="submit" fullWidth size="large" variant="contained" disabled={isLoading}
              sx={{ mt: 3, py: 1.5, borderRadius: 2, fontWeight: 750, textTransform: 'none' }}
            >
              {isLoading ? <CircularProgress size={25} color="inherit" />
                : mode === 'credentials' ? 'Iniciar sesión' : mode === 'challenge' ? 'Verificar' : 'Activar y continuar'}
            </Button> : <Button
              fullWidth size="large" variant="contained" sx={{ mt: 2, py: 1.5 }}
              onClick={() => completeAuthentication(pendingSession)}
            >
              Ya guardé los códigos
            </Button>}

            {mode !== 'credentials' && mode !== 'recovery' && <Button fullWidth sx={{ mt: 1 }} onClick={restart}>
              Volver al inicio
            </Button>}
          </Box>

          <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ mt: 3 }}>
            SMTLINKS Integrated Management
          </Typography>
        </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
}
